/* eslint-disable react-hooks/rules-of-hooks */

'use client';

import { NextRouter, useRouter as usePageRouter } from 'next/router';
import {
  useRouter as useAppRouter,
  usePathname,
  useSearchParams,
} from 'next/navigation';
import { Url } from 'next/dist/shared/lib/router/router';
import type { ParsedUrlQueryInput } from 'node:querystring';

const parsedUrlQueryToURLSearchParameters = (query: ParsedUrlQueryInput) => {
  const parameterList: [
    string,
    string | number | boolean | null | undefined,
  ][] = [];

  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const innerValue of value) parameterList.push([key, innerValue]);
    } else {
      parameterList.push([key, value as (typeof parameterList)[number][1]]);
    }
  }

  const definedParameterList = parameterList.filter(
    (parameter): parameter is [string, string | number | boolean] =>
      parameter[1] != null,
  );

  const parameters = new URLSearchParams();
  for (const [key, value] of definedParameterList)
    parameters.append(key, value.toString());
  return parameters;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const normalizeUrl = (url: Url) => {
  if (typeof url === 'string') return url;
  if (url.href) return url.href;

  let result = url.pathname ?? '';

  if (url.query && typeof url.query === 'object') {
    const parsedQueryString = parsedUrlQueryToURLSearchParameters(
      url.query,
    ).toString();

    result += (result.includes('?') ? '&' : '?') + parsedQueryString;
  } else if (typeof url.query === 'string') {
    const parsedQueryString = url.query.startsWith('?')
      ? url.query.slice(1)
      : url.query;

    result += (result.includes('?') ? '&' : '?') + parsedQueryString;
  } else if (url.search) {
    const parsedQueryString = url.search.startsWith('?')
      ? url.search
      : `?${url.search}`;

    result += parsedQueryString;
  }

  if (url.hash) {
    const hash = url.hash.startsWith('#') ? url.hash : `#${url.hash}`;
    result += hash;
  }

  return result;
};

export const useRouters = () => {
  try {
    return usePageRouter();
    // eslint-disable-next-line no-empty
  } catch {}

  const appRouter = useAppRouter();

  return {
    asPath: usePathname() ?? '',
    query:
      [...(useSearchParams() ?? [])].reduce(
        (accumulator, [key, value]) => ({
          ...accumulator,
          [key]: accumulator[key]
            ? [
                ...(Array.isArray(accumulator[key])
                  ? accumulator[key]
                  : [accumulator[key]]),
                value,
              ]
            : value,
        }),
        {} as NextRouter['query'],
      ) ?? {},
    ...appRouter,
    push: (url => appRouter.push(normalizeUrl(url))) as NextRouter['push'],
    replace: ((originalUrl, url) =>
      appRouter.replace(
        normalizeUrl(url ?? originalUrl),
      )) as NextRouter['replace'],
  };
};
